/**
 * 3D 颜色查找表（M4-03 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 与 `lut.ts` 的 1D LUT（逐通道独立映射）不同——3D LUT 把 RGB 立方体整体
 * 映射到另一个立方体，能表达通道之间的耦合（比如把阴影推蓝、高光推暖同时
 * 压饱和度），这是分级/胶片模拟类效果的标准做法，1D LUT 做不到。
 */

/** 一个 3D LUT：`size` 是每边采样点数，`data` 按 R 最快、G 次之、B 最慢展开，值域 [0,1]。 */
export interface Lut3D {
  readonly size: number;
  readonly data: Float32Array;
  /** 输入域下界/上界（三通道），.cube 的 DOMAIN_MIN/DOMAIN_MAX。默认 [0,0,0]/[1,1,1]。 */
  readonly domainMin: readonly [number, number, number];
  readonly domainMax: readonly [number, number, number];
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/** 立方体格点索引 → data 偏移。 */
function cellIndex(size: number, rx: number, gy: number, bz: number): number {
  return (bz * size * size + gy * size + rx) * 3;
}

/**
 * 三线性插值采样：输入是 sRGB 字节（0–255），先按 domain 归一化到 LUT 的
 * 单位坐标，再在最近的 8 个格点间插值。输出重新映射回字节。
 */
export function sampleLut3d(lut: Lut3D, r: number, g: number, b: number): [number, number, number] {
  const { size, data, domainMin, domainMax } = lut;
  const norm = (v: number, lo: number, hi: number): number => {
    const range = Math.max(1e-6, hi - lo);
    return clamp01((v / 255 - lo) / range);
  };
  const nr = norm(r, domainMin[0], domainMax[0]);
  const ng = norm(g, domainMin[1], domainMax[1]);
  const nb = norm(b, domainMin[2], domainMax[2]);

  const scale = size - 1; // size >= 2（构造函数保证），scale >= 1
  const fx = nr * scale;
  const fy = ng * scale;
  const fz = nb * scale;
  const x0 = Math.min(scale - 1, Math.floor(fx));
  const y0 = Math.min(scale - 1, Math.floor(fy));
  const z0 = Math.min(scale - 1, Math.floor(fz));
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const z1 = z0 + 1;
  const tx = fx - x0;
  const ty = fy - y0;
  const tz = fz - z0;

  const out: [number, number, number] = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const c000 = data[cellIndex(size, x0, y0, z0) + c]!;
    const c100 = data[cellIndex(size, x1, y0, z0) + c]!;
    const c010 = data[cellIndex(size, x0, y1, z0) + c]!;
    const c110 = data[cellIndex(size, x1, y1, z0) + c]!;
    const c001 = data[cellIndex(size, x0, y0, z1) + c]!;
    const c101 = data[cellIndex(size, x1, y0, z1) + c]!;
    const c011 = data[cellIndex(size, x0, y1, z1) + c]!;
    const c111 = data[cellIndex(size, x1, y1, z1) + c]!;
    const c00 = c000 * (1 - tx) + c100 * tx;
    const c10 = c010 * (1 - tx) + c110 * tx;
    const c01 = c001 * (1 - tx) + c101 * tx;
    const c11 = c011 * (1 - tx) + c111 * tx;
    const c0 = c00 * (1 - ty) + c10 * ty;
    const c1 = c01 * (1 - ty) + c11 * ty;
    const v = c0 * (1 - tz) + c1 * tz;
    out[c] = Math.round(clamp01(v) * 255);
  }
  return out;
}

/** 用一个 [0,1]→[0,1] 的映射函数烘出一个 `size`×`size`×`size` 的 3D LUT。 */
export function bakeLut3d(
  size: number,
  fn: (r: number, g: number, b: number) => readonly [number, number, number],
): Lut3D {
  const data = new Float32Array(size * size * size * 3);
  const scale = size - 1;
  for (let bz = 0; bz < size; bz++) {
    for (let gy = 0; gy < size; gy++) {
      for (let rx = 0; rx < size; rx++) {
        const [r, g, b] = fn(rx / scale, gy / scale, bz / scale);
        const at = cellIndex(size, rx, gy, bz);
        data[at] = clamp01(r);
        data[at + 1] = clamp01(g);
        data[at + 2] = clamp01(b);
      }
    }
  }
  return { size, data, domainMin: [0, 0, 0], domainMax: [1, 1, 1] };
}

export function identityLut3d(size = 2): Lut3D {
  return bakeLut3d(size, (r, g, b) => [r, g, b]);
}

/** 预设：冷色调——阴影推蓝、高光略压饱和。 */
export function coolToneLut3d(): Lut3D {
  return bakeLut3d(9, (r, g, b) => {
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    const shadowLift = (1 - luma) * 0.12;
    return [clamp01(r - shadowLift * 0.5), clamp01(g - shadowLift * 0.2), clamp01(b + shadowLift)];
  });
}

/** 预设：暖色调——阴影推橙、整体略提红黄。 */
export function warmToneLut3d(): Lut3D {
  return bakeLut3d(9, (r, g, b) => {
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    const shadowLift = (1 - luma) * 0.12;
    return [clamp01(r + shadowLift), clamp01(g + shadowLift * 0.4), clamp01(b - shadowLift * 0.6)];
  });
}
